import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../design-system/primitives/Icon';
import { Button } from '../ui/button';
import { apiService } from '../../services/api';
import WarehouseBrowser from './WarehouseBrowser';

const TYPES = [
  { key: 'snowflake', label: 'Snowflake', icon: 'snowflake' },
  { key: 'bigquery', label: 'BigQuery', icon: 'cloud' },
  { key: 'databricks', label: 'Databricks', icon: 'boxes' },
  { key: 'redshift', label: 'Amazon Redshift', icon: 'database-zap' },
];

const DOCS = {
  snowflake: 'docs/connectors/snowflake-setup.md',
  bigquery: 'docs/connectors/bigquery-setup.md',
  databricks: 'docs/connectors/databricks-setup.md',
  redshift: 'docs/connectors/redshift-setup.md',
};

/**
 * "Connect a data source" modal: manage warehouse connections (create with
 * per-type forms + test-on-save, delete, re-test) and, with a verified
 * connection selected, browse tables / run SQL and import into the analysis.
 * `initialConnectionId` opens straight into that connection's browser when it
 * is verified (the rail's Sources rows); otherwise the list shows.
 */
const ConnectSourceModal = ({
  open, onClose, analysisId, ensureAnalysisExists, onImported, toast, initialConnectionId = null,
}) => {
  const [connections, setConnections] = useState(null);
  const [fieldSpecs, setFieldSpecs] = useState({});
  const [view, setView] = useState('list');       // 'list' | 'create' | 'browse'
  const [createType, setCreateType] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [active, setActive] = useState(null);      // connection being browsed
  const [resolvedAnalysisId, setResolvedAnalysisId] = useState(analysisId);
  const pendingConnRef = useRef(null);

  const load = useCallback(() => {
    apiService.listConnections()
      .then((resp) => {
        setConnections(resp.data.connections || []);
        setFieldSpecs(resp.data.field_specs || {});
      })
      .catch(() => setConnections([]));
  }, []);

  useEffect(() => {
    if (open) {
      setView('list');
      setActive(null);
      setFormError(null);
      setResolvedAnalysisId(analysisId);
      pendingConnRef.current = initialConnectionId;
      load();
    }
  }, [open, analysisId, initialConnectionId, load]);

  const startBrowse = useCallback(async (conn) => {
    let id = resolvedAnalysisId;
    if (!id && ensureAnalysisExists) {
      try {
        id = await ensureAnalysisExists();
        setResolvedAnalysisId(id);
      } catch {
        if (toast) toast('Could not create the analysis.', 'error');
        return;
      }
    }
    setActive(conn);
    setView('browse');
  }, [resolvedAnalysisId, ensureAnalysisExists, toast]);

  // Jump into the requested connection once the list has it.
  useEffect(() => {
    if (!open || !connections || pendingConnRef.current == null) return;
    const conn = connections.find((c) => c.id === pendingConnRef.current);
    pendingConnRef.current = null;
    if (conn && conn.status === 'ok') startBrowse(conn);
  }, [open, connections, startBrowse]);

  const handleCreate = useCallback(async () => {
    if (saving || !createType) return;
    setSaving(true);
    setFormError(null);
    try {
      const resp = await apiService.createConnection({ connection_type: createType, ...form });
      setForm({});
      load();
      const conn = resp.data.connection;
      if (conn?.status === 'ok') {
        startBrowse(conn);
      } else {
        setView('list');
      }
    } catch (e) {
      const conn = e?.response?.data?.connection;
      setFormError(conn?.last_error || e?.response?.data?.detail || 'Could not create the connection.');
      if (conn) load(); // saved but failed verification — show it in the list
    } finally {
      setSaving(false);
    }
  }, [saving, createType, form, load, startBrowse]);

  const handleDelete = useCallback(async (conn) => {
    if (!window.confirm(`Remove the connection "${conn.name}"? Imported datasets stay.`)) return;
    try {
      await apiService.deleteConnection(conn.id);
      load();
    } catch {
      if (toast) toast('Could not remove the connection.', 'error');
    }
  }, [load, toast]);

  const handleTest = useCallback(async (conn) => {
    try {
      await apiService.testConnection(conn.id);
    } finally {
      load();
    }
  }, [load]);

  if (!open) return null;

  const spec = createType ? fieldSpecs[createType] : null;
  const formFields = spec ? [...(spec.config || []), ...(spec.secrets || [])] : [];

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'grid', placeItems: 'center', padding: 24,
      }}
    >
      <div style={{
        width: view === 'browse' ? 'min(1060px, 96vw)' : 'min(640px, 96vw)',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 12, boxShadow: 'none', padding: 24, gap: 16,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {view !== 'list' && (
            <button
              onClick={() => { setView('list'); setActive(null); setFormError(null); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)', display: 'inline-flex', padding: 4 }}
            >
              <Icon name="arrow-left" size={17} />
            </button>
          )}
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-soft)', display: 'grid', placeItems: 'center' }}>
            <Icon name="database" size={18} color="var(--accent)" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg-strong)' }}>
              {view === 'browse' && active ? `${active.name}` : 'Connect a data source'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>
              {view === 'browse'
                ? 'Pick a table or write a read-only query, then import it into this analysis.'
                : 'Pull data straight from your data warehouse.'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--fg-subtle)', padding: 4 }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Body */}
        {view === 'list' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
            {(connections || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {connections.map((c) => (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    border: '1px solid var(--border)', borderRadius: 12, background: 'var(--card)',
                  }}>
                    <Icon name={(TYPES.find(t => t.key === c.connection_type) || {}).icon || 'database'} size={16} color="var(--accent)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-strong)' }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: c.status === 'error' ? 'var(--red, #d5484f)' : 'var(--fg-subtle)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.status === 'ok' && `Connected · ${c.credential_summary?.auth || ''}`}
                        {c.status === 'error' && (c.last_error || 'Connection failed')}
                        {c.status === 'unverified' && 'Not verified yet'}
                      </div>
                    </div>
                    {c.status !== 'ok' && (
                      <Button variant="outline" size="sm" onClick={() => handleTest(c)} title="Re-test">
                        Re-test
                      </Button>
                    )}
                    {c.status === 'ok' && (
                      <Button size="sm" onClick={() => startBrowse(c)}>
                        Browse
                      </Button>
                    )}
                    <button onClick={() => handleDelete(c)} title="Remove"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--fg-subtle)', padding: 4 }}>
                      <Icon name="trash-2" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 12, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 500 }}>
              {connections && connections.length > 0 ? 'Add another' : 'Choose your warehouse'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setCreateType(t.key); setForm({}); setFormError(null); setView('create'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
                    background: 'var(--bg-elev-2)', border: '1px solid var(--line-soft)', borderRadius: 12,
                    color: 'var(--fg)', fontSize: 14, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line-soft)'; }}
                >
                  <Icon name={t.icon} size={16} color="var(--accent)" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {view === 'create' && spec && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-strong)' }}>
              {(TYPES.find(t => t.key === createType) || {}).label}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-subtle)', lineHeight: 1.5 }}>
              Use a <strong>read-only</strong> role — Qombra never writes to your warehouse
              unless you explicitly configure a prediction destination in Manage Pipeline
              (that connection then also needs INSERT / CREATE TABLE rights on the target schema).
              Setup guide: <span style={{ fontFamily: 'var(--font-mono)' }}>{DOCS[createType]}</span>
            </div>
            <Field label="Connection name" value={form.name || ''} onChange={(v) => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Production warehouse" />
            {formFields.map((f) => (
              <Field
                key={f.name}
                label={f.label + (f.required ? '' : ' (optional)')}
                value={form[f.name] || ''}
                onChange={(v) => setForm(prev => ({ ...prev, [f.name]: v }))}
                multiline={f.multiline}
                secret={f.secret}
                placeholder={f.default != null ? String(f.default) : undefined}
              />
            ))}
            {formError && <div style={{ fontSize: 13, color: 'var(--red, #d5484f)' }}>{formError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <Button variant="outline" onClick={() => setView('list')}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving && <Icon name="loader-2" size={14} style={{ animation: 'qSpin 800ms linear infinite' }} />}
                {saving ? 'Testing…' : 'Test & save'}
              </Button>
            </div>
          </div>
        )}

        {view === 'browse' && active && (
          <WarehouseBrowser
            connection={active}
            analysisId={resolvedAnalysisId}
            onImported={onImported}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, secret, placeholder }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 12, color: 'var(--fg-muted)', fontWeight: 500 }}>{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        spellCheck={false}
        placeholder={placeholder}
        style={{
          padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elev-2)',
          border: '1px solid var(--line-soft)', color: 'var(--fg-strong)', fontSize: 13,
          fontFamily: 'var(--font-mono)', outline: 'none', resize: 'vertical',
        }}
      />
    ) : (
      <input
        type={secret ? 'password' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          padding: '10px 12px', borderRadius: 10, background: 'var(--bg-elev-2)',
          border: '1px solid var(--line-soft)', color: 'var(--fg-strong)', fontSize: 14, outline: 'none',
        }}
      />
    )}
  </label>
);

export default ConnectSourceModal;
